/**
 * Utility to format audit log values for human-readable display
 * Converts enum numeric values to their string labels
 */

// Enum mappings - numeric value to label
const ENUM_MAPPINGS: Record<string, Record<number, string>> = {
  // Project Status
  Status: {
    0: 'Draft',
    1: 'Submitted',
    2: 'Recruitment',
    3: 'Identified',
    4: 'Proposed',
    5: 'InProgress',
    6: 'Delivered',
    7: 'Completed',
    8: 'Closed',
    9: 'Archived',
  },
  ProjectStatus: {
    0: 'Draft',
    1: 'Submitted',
    2: 'Recruitment',
    3: 'Identified',
    4: 'Proposed',
    5: 'InProgress',
    6: 'Delivered',
    7: 'Completed',
    8: 'Closed',
    9: 'Archived',
  },

  // Project Freelancer Status
  ProjectFreelancerStatus: {
    0: 'NotSubmitted',
    1: 'SubmittedCSM',
    2: 'InDiscussion',
    3: 'Revised',
    4: 'CastingScheduled',
    5: 'Accepted',
    6: 'RefusedClient',
    7: 'RefusedTalent',
    8: 'SubmittedClient',
    9: 'Cancelled',
  },

  // Freelancer Proposal Status
  FreelancerProposalStatus: {
    0: 'Draft',
    1: 'SubmittedCSM',
    2: 'InDiscussion',
    3: 'Revised',
    4: 'CastingScheduled',
    5: 'Accepted',
    6: 'RefusedClient',
    7: 'RefusedTalent',
    8: 'SubmittedClient',
    9: 'Cancelled',
  },

  // Freelancer Onboarding Status
  FreelancerOnboardingStatus: {
    0: 'NotSubmitted',
    1: 'Submitted',
    2: 'Interview',
    3: 'SoftSkillsEvaluation',
    4: 'HardSkillsEvaluation',
    5: 'Positioning',
    6: 'Accepted',
    7: 'Refused',
  },
  OnboardingStatus: {
    0: 'NotSubmitted',
    1: 'Submitted',
    2: 'Interview',
    3: 'SoftSkillsEvaluation',
    4: 'HardSkillsEvaluation',
    5: 'Positioning',
    6: 'Accepted',
    7: 'Refused',
  },

  // User Type
  UserType: {
    0: 'Internal',
    1: 'Client',
    2: 'Freelancer',
  },
  Type: {
    0: 'Internal',
    1: 'Client',
    2: 'Freelancer',
  },

  // User Status
  UserStatus: {
    0: 'Active',
    1: 'Inactive',
    2: 'Pending',
  },

  // Project Domain
  ProjectDomain: {
    0: 'Data',
    1: 'Insight',
    2: 'Both',
  },
  Domain: {
    0: 'Data',
    1: 'Insight',
    2: 'Both',
  },

  // Project Type
  ProjectType: {
    0: 'FixedPrice',
    1: 'TimeAndMaterial',
  },

  // Duration Type
  DurationType: {
    0: 'Weeks',
    1: 'Months',
  },

  // Project Location
  ProjectLocation: {
    0: 'OnSite',
    1: 'Remote',
    2: 'Hybrid',
    3: 'NoPreference',
  },
  Location: {
    0: 'OnSite',
    1: 'Remote',
    2: 'Hybrid',
    3: 'NoPreference',
  },

  // Mission Type
  MissionType: {
    0: 'BusinessExpert',
    1: 'ManagementTransition',
    2: 'TalentOnDemand',
    3: 'TalentPoolOnDemand',
  },

  // Freelancer Activity Report Status
  FreelancerActivityReportStatus: {
    0: 'Draft',
    1: 'Submitted',
    2: 'Validated',
    3: 'Rejected',
  },

  // Skill Level
  SkillLevel: {
    0: 'Beginner',
    1: 'Intermediate',
    2: 'Advanced',
    3: 'Expert',
  },
  Level: {
    0: 'Beginner',
    1: 'Intermediate',
    2: 'Advanced',
    3: 'Expert',
  },

  // Tool Level
  ToolLevel: {
    0: 'Beginner',
    1: 'Intermediate',
    2: 'Advanced',
    3: 'Expert',
  },

  // Language Proficiency
  LanguageProficiency: {
    0: 'Basic',
    1: 'Intermediate',
    2: 'Advanced',
    3: 'Native',
  },
  Proficiency: {
    0: 'Basic',
    1: 'Intermediate',
    2: 'Advanced',
    3: 'Native',
  },

  // Language Code
  LanguageCode: {
    0: 'Fr',
    1: 'En',
  },
  PreferredLanguage: {
    0: 'Fr',
    1: 'En',
  },

  // Communication Channel
  CommunicationChannel: {
    0: 'Email',
    1: 'Phone',
    2: 'Teams',
    3: 'Slack',
    4: 'WhatsApp',
  },

  // Freelancer Frequency
  FreelancerFrequency: {
    0: 'Never',
    1: 'Rarely',
    2: 'Sometimes',
    3: 'Often',
    4: 'Always',
  },

  // Company Type
  CompanyType: {
    0: 'Startup',
    1: 'SME',
    2: 'Enterprise',
    3: 'Consulting',
  },

  // Company Size
  CompanySize: {
    0: 'Small',
    1: 'Medium',
    2: 'Large',
    3: 'Enterprise',
  },

  // Industry Type
  IndustryType: {
    0: 'Technology',
    1: 'Finance',
    2: 'Healthcare',
    3: 'Retail',
    4: 'Manufacturing',
    5: 'Services',
    6: 'Other',
  },

  // Document Type
  DocumentType: {
    0: 'CV',
    1: 'Portfolio',
    2: 'Certificate',
    3: 'Other',
  },

  // URL Type
  UrlType: {
    0: 'LinkedIn',
    1: 'GitHub',
    2: 'Portfolio',
    3: 'Other',
  },

  // Rate Type
  RateType: {
    0: 'Hourly',
    1: 'Daily',
    2: 'Monthly',
  },

  // Freelancer Decline Reason
  FreelancerDeclineReason: {
    0: 'NotInterested',
    1: 'RateTooLow',
    2: 'NotAvailable',
    3: 'LocationIssue',
    4: 'Other',
  },

  // Client Decline Reason
  ClientDeclineReason: {
    0: 'NotFitting',
    1: 'TooExpensive',
    2: 'BetterCandidate',
    3: 'ProjectCancelled',
    4: 'Other',
  },

  // Proposal Workflow Action
  ProposalWorkflowAction: {
    0: 'Submit',
    1: 'Approve',
    2: 'Reject',
    3: 'RequestChanges',
    4: 'Cancel',
  },

  // Client Proposal Action
  ClientProposalAction: {
    0: 'Accept',
    1: 'Reject',
    2: 'RequestChanges',
  },

  // Freelancer Onboarding Form Step
  FreelancerOnboardingFormStep: {
    0: 'PersonalInfo',
    1: 'ProfessionalInfo',
    2: 'Skills',
    3: 'Experience',
    4: 'Documents',
    5: 'Review',
  },

  // Soft Skills Evaluation Status
  FreelancerSoftSkillsEvaluationStatus: {
    0: 'Pending',
    1: 'Completed',
    2: 'Cancelled',
  },

  // Proposal Conversation Message Type
  ProposalConversationMessageType: {
    0: 'Message',
    1: 'SystemMessage',
    2: 'StatusChange',
  },
};

// Fields that are known to be IDs and should show "ID: X" format
const ID_FIELDS = [
  'Id',
  'UserId',
  'CompanyId',
  'ProjectId',
  'FreelancerId',
  'ClientId',
  'FreelancerProfileId',
  'CreatedById',
  'UpdatedById',
  'ProposalId',
  'FreelancerProposalId',
];

// Fields that contain dates
const DATE_FIELDS = [
  'CreatedAt',
  'UpdatedAt',
  'StartDate',
  'EndDate',
  'DesiredStartDate',
  'Timestamp',
  'ExpiresAt',
];

/**
 * Format a single value based on its field name
 */
export const formatAuditValue = (fieldName: string, value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  // Check if it's a boolean
  if (typeof value === 'boolean') {
    return value ? '✓ Yes' : '✗ No';
  }

  // Check if it's a number that might be an enum
  if (typeof value === 'number') {
    // Check if field has an enum mapping
    const mapping = ENUM_MAPPINGS[fieldName];
    if (mapping && mapping[value] !== undefined) {
      return `${mapping[value]} (${value})`;
    }

    // Check if it's an ID field
    if (ID_FIELDS.some(idField => fieldName.includes(idField))) {
      return `#${value}`;
    }

    return String(value);
  }

  // Check if it's a date string
  if (typeof value === 'string') {
    if (DATE_FIELDS.some(dateField => fieldName.includes(dateField))) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString();
        }
      } catch {
        // Not a valid date, return as-is
      }
    }
    return value;
  }

  // For objects/arrays, stringify
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Format an entire audit log JSON object with human-readable values
 */
export const formatAuditObject = (obj: Record<string, unknown> | null): Record<string, string> | null => {
  if (!obj) return null;

  const formatted: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    formatted[key] = formatAuditValue(key, value);
  }
  return formatted;
};

/**
 * Parse and format JSON string from audit log
 */
export const parseAndFormatAuditJson = (jsonString: string | null): string => {
  if (!jsonString) return '';

  try {
    const obj = JSON.parse(jsonString);
    const formatted = formatAuditObject(obj);
    return JSON.stringify(formatted, null, 2);
  } catch {
    return jsonString;
  }
};

/**
 * Get a comparison view of old vs new values with formatted display
 */
export const getFormattedChanges = (
  oldValuesJson: string | null,
  newValuesJson: string | null
): Array<{ field: string; oldValue: string; newValue: string }> => {
  const changes: Array<{ field: string; oldValue: string; newValue: string }> = [];

  try {
    const oldValues = oldValuesJson ? JSON.parse(oldValuesJson) : {};
    const newValues = newValuesJson ? JSON.parse(newValuesJson) : {};

    // Get all unique keys
    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);

    for (const key of allKeys) {
      const oldVal = oldValues[key];
      const newVal = newValues[key];

      // Only show if values are different
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          oldValue: formatAuditValue(key, oldVal),
          newValue: formatAuditValue(key, newVal),
        });
      }
    }
  } catch {
    // Return empty array if parsing fails
  }

  return changes;
};
